import React, { useState, useEffect, useRef } from 'react';
import { getFirestore, addDoc, collection } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ReCAPTCHA from 'react-google-recaptcha';
import Compressor from 'compressorjs';
import SocialShare from '../components/share';
import { Trans } from 'react-i18next';
import { isHeic, heicTo } from "heic-to";

import {
  SlAlert,
  SlBreadcrumb,
  SlBreadcrumbItem,
  SlCheckbox,
  SlIcon,
  SlInput,
  SlTextarea,
} from '@shoelace-style/shoelace/dist/react';
import { Link, useLoaderData } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import Button from '../components/button.component';
import MediaDropComponent from '../components/MediaDrop.component';
import { CardStyles } from '../components/card.styles';
import { CHALLENGE_CONFIG } from '../challenge';

function AddEntry() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    date: '',
    comment: '',
    newsletter: false,
    termsAccepted: false
  });

  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [uploadStatus, setUploadStatus] = useState({
    phase: '', // 'verifying' | 'uploading' | 'saving' | 'complete'
    progress: 0,
    message: ''
  });
  const [isSubmissionComplete, setIsSubmissionComplete] = useState(false);

  const db = getFirestore();
  const storage = getStorage();
  const functions = getFunctions(undefined, 'europe-west6');

  const collectionName = (process.env.NODE_ENV === 'development' || window.location.hostname.includes('staging') || window.location.hostname.includes('glacier-challenge')) ? 'data-dev' : 'data';
  const mountainsCollectionRef = collection(db, collectionName);
  const { t, i18n } = useTranslation('app');
  const mountain = useLoaderData();
  // const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (isSubmissionComplete) {
      window.open(t('form.donateButtonLink'), '_blank');
    }
  }, [isSubmissionComplete, t]);

  // Update form data handler
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Date validation
  const validateDate = (dateString) => {
    const selectedDate = new Date(dateString);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    const startDate = new Date(CHALLENGE_CONFIG.startDate);
    const endDate = new Date(CHALLENGE_CONFIG.endDate);

    return {
      isValid: true,
      tooEarly: selectedDate < startDate,
      tooLate: selectedDate > endDate,
      inFuture: selectedDate > today
    };
  };

  // File type validation
  const isValidVideoFile = (file) => {
    const validExtensions = ['.mp4', '.mov', '.mpeg', '.mpg', '.avi', '.webm', '.mkv', '.m4v'];
    const hasValidMimeType = file.type.startsWith('video/');
    const hasValidExtension = validExtensions.includes(
      file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    );
    return hasValidMimeType || hasValidExtension;
  };

  const isValidImageFile = (file) => {
    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif'];
    const hasValidMimeType = file.type.startsWith('image/');
    const hasValidExtension = validExtensions.includes(
      file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
    );
    return hasValidMimeType || hasValidExtension;
  };

  // Validate all form data
  const validateForm = () => {
    const newErrors = [];
    const isDev = process.env.NODE_ENV === 'development' || window.location.hostname.includes('staging');

    // Required fields
    if (!formData.firstName.trim()) {
      newErrors.push({ message: 'errors.noFirstName' });
    }

    if (!formData.lastName.trim()) {
      newErrors.push({ message: 'errors.noLastName' });
    }

    // Require either media or comment
    if (mediaFiles.length === 0 && !formData.comment.trim()) {
      newErrors.push({ message: 'errors.noMediaOrComment' });
    }

    if (!formData.email.trim()) {
      newErrors.push({ message: 'errors.noEmail' });
    } else if (!validateEmail(formData.email)) {
      newErrors.push({ message: 'errors.invalidEmail' });
    }

    if (!formData.date) {
      newErrors.push({ message: 'errors.noDate' });
    } else {
      const dateValidation = validateDate(formData.date);
      if (!isDev) {
        if (dateValidation.tooEarly) {
          newErrors.push({ message: 'errors.dateTooEarly' });
        } else if (dateValidation.tooLate) {
          newErrors.push({ message: 'errors.dateTooOld' });
        } else if (dateValidation.inFuture) {
          newErrors.push({ message: 'errors.dateInFuture' });
        }
      }
    }

    if (!formData.termsAccepted) {
      newErrors.push({ message: 'errors.noCheckbox' });
    }

    // File validation
    const maxVideoSize = 100 * 1024 * 1024; // 100MB
    const maxImageSize = 10 * 1024 * 1024; // 10MB
    const maxTotalSize = 100 * 1024 * 1024; // 100MB total per contribution

    let totalSize = 0;
    mediaFiles.forEach(file => {
      const isVideo = isValidVideoFile(file);
      const isImage = isValidImageFile(file);

      // Check if file type is valid
      if (!isVideo && !isImage) {
        newErrors.push({
          message: 'errors.invalidFileType',
          fileName: file.name
        });
        return;
      }

      const maxSize = isVideo ? maxVideoSize : maxImageSize;
      totalSize += file.size;

      if (file.size > maxSize) {
        newErrors.push({
          message: isVideo ? 'errors.videoTooLarge' : 'errors.imageTooLarge',
          fileName: file.name
        });
      }
    });

    // Check total contribution size
    if (totalSize > maxTotalSize) {
      newErrors.push({
        message: 'errors.totalSizeTooLarge',
        totalSize: `${(totalSize / 1024 / 1024).toFixed(2)}MB`
      });
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Compress image
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      new Compressor(file, {
        maxWidth: 1500,
        quality: 0.8,
        success: resolve,
        error: reject
      });
    });
  };

  // Upload single media file
  const uploadMediaFile = async (file, index, total) => {
    try {
      // Update progress with translated message
      setUploadStatus(prev => ({
        ...prev,
        phase: 'uploading',
        progress: Math.round(((index - 1) / total * 80) + ((1 / total) * 80)),
        message: t('form.uploadingFile', { fileName: file.name })
      }));

      // Convert HEIC to JPEG if needed
      let processedFile = file;
      const isHeicFile = await isHeic(file) || file.name.toLowerCase().match(/\.(heic|heif)$/);
      if (isHeicFile) {
        const jpegBlob = await heicTo({
          blob: file,
          type: "image/jpeg",
          quality: 0.9
        });
        const jpegFileName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
        processedFile = new File([jpegBlob], jpegFileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
      }

      // Clean filename
      const timestamp = Date.now();
      const cleanName = processedFile.name.replace(/[^a-zA-Z0-9.]/g, '_');
      const fileName = `${timestamp}-${cleanName}`;

      // Determine file type and folder
      const isVideo = isValidVideoFile(processedFile);
      const folder = process.env.NODE_ENV === 'development' || window.location.hostname.includes('staging')
        ? (isVideo ? 'videos-dev' : 'images-dev')
        : (isVideo ? 'videos' : 'images');

      // Prepare file for upload
      let fileToUpload = processedFile;
      if (!isVideo && isValidImageFile(processedFile)) {
        fileToUpload = await compressImage(processedFile);
      }

      // Upload file
      const fileRef = ref(storage, `${folder}/${fileName}`);
      await uploadBytes(fileRef, fileToUpload);
      const url = await getDownloadURL(fileRef);

      return {
        url, // For videos, this will be the original URL that gets replaced with compressed
        thumbnailUrl: null, // Will be set by Cloud Function for videos
        type: isVideo ? 'video' : 'image',
        originalName: file.name,
        size: file.size,
        fileName: fileName
      };
    } catch (error) {
      console.error(`Error uploading ${file.name}:`, error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      // Verify reCAPTCHA
      setUploadStatus({
        phase: 'verifying',
        progress: 0,
        message: t('form.verifyingRecaptcha')
      });

      const token = await recaptchaRef.current.executeAsync();
      const verifyRecaptcha = httpsCallable(functions, 'verifyRecaptchaV2');
      await verifyRecaptcha({ recaptchaToken: token });

      // Upload media files
      const mediaUrls = [];

      if (mediaFiles.length > 0) {
        for (let i = 0; i < mediaFiles.length; i++) {
          const mediaData = await uploadMediaFile(mediaFiles[i], i + 1, mediaFiles.length);
          mediaUrls.push(mediaData);
        }
      }

      // Save to Firestore
      setUploadStatus(prev => ({
        ...prev,
        phase: 'saving',
        progress: 85,
        message: t('form.savingEntry')
      }));

      await addDoc(mountainsCollectionRef, {
        uuid: uuidv4(),
        mountainId: mountain.id,
        mediaUrls, // Videos will be processed in background
        // Form data
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        date: formData.date,
        comment: formData.comment.trim(),
        newsletter: formData.newsletter,
        createdAt: new Date(),
        userLocale: i18n.language,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown'
      });

      // Success!
      setUploadStatus({
        phase: 'complete',
        progress: 100,
        message: t('form.success')
      });

      // Set submission complete state instead of navigating
      setIsSubmissionComplete(true);

    } catch (error) {
      console.error('Submission error:', error);
      setErrors([{ message: 'errors.uploadFailed' }]);
      recaptchaRef.current.reset();
    } finally {
      setLoading(false);
    }
  };

  // If submission is complete, show success message and buttons
  if (isSubmissionComplete) {
    return (
      <div className="add-entry-container">
        <SlBreadcrumb>
          <span slot="separator">/</span>
          <SlBreadcrumbItem>
            <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
              {t('common.all')}
            </Link>
          </SlBreadcrumbItem>
          <SlBreadcrumbItem>
            <Link
              to={`/glaciers/${mountain.id}`}
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              {mountain.name}
            </Link>
          </SlBreadcrumbItem>
          <SlBreadcrumbItem>{t('common.add')}</SlBreadcrumbItem>
        </SlBreadcrumb>

        <h1>{mountain.name}</h1>
        <div>
          <h3>
            {t('form.successTitle')}
          </h3>

          <p>
            <Trans
              i18nKey={t('form.successMessage')}
              components={{ br: <br /> }}
            />
          </p>

          <div style={{ margin: '1rem 0' }}>
            <a href={t('form.donateButtonLink')}>
              <Button target="_blank" rel="noopener noreferrer">
                {t('form.donateButton')}
              </Button>
            </a>
          </div>

          <SocialShare url={t('header.campaignLink')} title={t('social.description')} />
        </div>
      </div>
    );
  }


  return (
    <div className="add-entry-container">
      <SlBreadcrumb>
        <span slot="separator">/</span>
        <SlBreadcrumbItem>
          <Link to="/" style={{ color: 'inherit', textDecoration: 'none' }}>
            {t('common.all')}
          </Link>
        </SlBreadcrumbItem>
        <SlBreadcrumbItem>
          <Link
            to={`/glaciers/${mountain.id}`}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            {mountain.name}
          </Link>
        </SlBreadcrumbItem>
        <SlBreadcrumbItem>{t('common.add')}</SlBreadcrumbItem>
      </SlBreadcrumb>

      <h1>{mountain.name}</h1>

      <CardStyles>
        <div style={{ margin: '15px 0' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
            {t('form.firstName')} <span style={{ color: 'red' }}>*</span>
          </label>
          <SlInput
            value={formData.firstName}
            onSlChange={(e) => updateFormData('firstName', e.target.value)}
            disabled={loading}
          />
        </div>

        <div style={{ margin: '15px 0' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
            {t('form.lastName')} <span style={{ color: 'red' }}>*</span>
          </label>
          <SlInput
            value={formData.lastName}
            onSlChange={(e) => updateFormData('lastName', e.target.value)}
            disabled={loading}
          />
        </div>

        <div style={{ margin: '15px 0' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
            {t('form.email')} <span style={{ color: 'red' }}>*</span>
          </label>
          <SlInput
            type="email"
            value={formData.email}
            onSlChange={(e) => updateFormData('email', e.target.value)}
            disabled={loading}
          />
        </div>

        <div style={{ margin: '15px 0' }}>
          <label style={{ fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
            {t('form.date')} <span style={{ color: 'red' }}>*</span>
          </label>
          <SlInput
            type="date"
            value={formData.date}
            onSlChange={(e) => updateFormData('date', e.target.value)}
            disabled={loading}
          />
        </div>

        <MediaDropComponent
          style={{ margin: '15px 0' }}
          onChange={setMediaFiles}
          disabled={loading}
        />

        {/* File size info */}
        {mediaFiles.length > 0 && (
          <div style={{ margin: '10px 0', fontSize: '0.875rem', color: '#666' }}>
            {t('form.totalSize')}: {(mediaFiles.reduce((sum, file) => sum + file.size, 0) / 1024 / 1024).toFixed(2)}MB / 100MB
          </div>
        )}

        {/* Upload Progress */}
        {loading && uploadStatus.phase && (
          <div style={{ margin: '10px 0' }}>
            <div>{uploadStatus.message}</div>
            <div style={{
              width: '100%',
              height: '10px',
              backgroundColor: '#eee',
              borderRadius: '5px',
              marginTop: '5px'
            }}>
              <div style={{
                width: `${uploadStatus.progress}%`,
                height: '100%',
                backgroundColor: '#4CAF50',
                borderRadius: '5px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        )}

        <div style={{ margin: '15px 0' }}>
          <label style={{ marginBottom: '0.5rem', display: 'block' }}>
            {t('form.comments')}
          </label>
          <SlTextarea
            value={formData.comment}
            onSlChange={(e) => updateFormData('comment', e.target.value)}
            disabled={loading}
            rows={4}
          />
        </div>

        <div style={{ margin: '0 0 2rem' }}>
          <SlCheckbox
            checked={formData.termsAccepted}
            onSlChange={(e) => updateFormData('termsAccepted', e.target.checked)}
            disabled={loading}
          >
            <span style={{ color: errors.some(e => e.message === 'errors.noCheckbox') ? 'red' : 'inherit' }}>
              {t('form.checkbox')} <span style={{ color: 'red' }}>*</span>
            </span>
          </SlCheckbox>
        </div>

        <div style={{ margin: '0 0 2rem' }}>
          <SlCheckbox
            checked={formData.newsletter}
            onSlChange={(e) => updateFormData('newsletter', e.target.checked)}
            disabled={loading}
          >
            <span>{t('form.newsletter')}</span>
          </SlCheckbox>
        </div>

        <div style={{ margin: '0 0 2rem' }}>
          <span style={{ color: 'red' }}>*</span> {t('form.required')}
        </div>

        <div style={{ margin: '0 0 1rem', fontSize: '0.875rem', color: '#666' }}>
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
            Terms of Service
          </a>{' '}
          apply.
        </div>

        {errors.length > 0 && (
          <SlAlert variant="warning" open style={{ marginBottom: '1rem' }}>
            <SlIcon slot="icon" name="exclamation-triangle" />
            <strong>{t('errors.error')}</strong>
            <br />
            <ul style={{ margin: '0', padding: '0 0 0 1rem' }}>
              {errors.map((error, i) => (
                <li key={i}>
                  {t(error.message)}
                  {error.fileName && ` (${error.fileName})`}
                  {error.totalSize && ` - ${error.totalSize}`}
                </li>
              ))}
            </ul>
          </SlAlert>
        )}

        <Button click={handleSubmit} loading={loading}>
          {mountain.votes > 0
            ? t('form.glacierExists')
            : t('form.upload')}
        </Button>

        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
          badge="bottomright"
        />
      </CardStyles>

      <style>{`
        .grecaptcha-badge { 
          visibility: hidden !important;
        }
        
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default AddEntry;